import React from 'react';
import type { Employee, Navigate } from '../../types/hr';
import { directReports, displayName, getEmployee } from '../../utils/employees';
import { Avatar } from '../ui/Avatar';
import { Modal } from '../ui/Modal';
interface OrgChartModalProps {
  open: boolean;
  employee: Employee;
  onClose: () => void;
  onNavigate: Navigate;
}
export function OrgChartModal({
  open,
  employee,
  onClose,
  onNavigate
}: OrgChartModalProps) {
  const manager = getEmployee(employee.managerId);
  const reports = directReports(employee.id);
  const go = (id: string) => {
    onClose();
    onNavigate({
      page: 'profile',
      employeeId: id
    });
  };
  return <Modal open={open} onClose={onClose} title="Org chart" description={`Reporting line for ${displayName(employee)}`} data-id="element-264" data-mp="3ren794vck99t">
      <div className="flex flex-col items-center" data-id="element-265" data-mp="9equyx2gepy">
        {manager ? <Node employee={manager} label="Manager" onClick={() => go(manager.id)} data-id="element-266" data-mp="58hz0s8cf125" /> : <p className="rounded-xl bg-canvas px-4 py-2 text-xs text-muted" data-id="element-267" data-mp="a5zxtpsnbzwf">Top of organization</p>}
        <Connector data-id="element-268" data-mp="1uoa6yi41ax55" />
        <Node employee={employee} label="Selected" highlighted data-id="element-269" data-mp="1pnw033e65t8p" />
        {reports.length > 0 && <>
            <Connector data-id="element-270" data-mp="1kojc2fbkag4j" />
            <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted" data-id="element-271" data-mp="1kpuhskokhxya">Direct reports · {reports.length}</p>
            <div className="grid max-h-64 w-full grid-cols-1 gap-2 overflow-y-auto sm:grid-cols-2" data-id="element-272" data-mp="1gp0rjknnk728">
              {reports.map(r => <Node key={r.id} employee={r} onClick={() => go(r.id)} compact data-id="element-273" data-mp="28enieug4wtz8" />)}
            </div>
          </>}
        {reports.length === 0 && <p className="mt-4 text-xs text-muted" data-id="element-274" data-mp="23lueu9wejroq">No direct reports</p>}
      </div>
    </Modal>;
}
interface NodeProps {
  employee: Employee;
  label?: string;
  highlighted?: boolean;
  compact?: boolean;
  onClick?: () => void;
}
function Node({
  employee,
  label,
  highlighted,
  compact,
  onClick
}: NodeProps) {
  const name = displayName(employee);
  const content = <>
      <Avatar name={name} seed={employee.id} size={compact ? 'sm' : 'md'} className={highlighted ? 'ring-2 ring-white/40' : ''} data-id="element-275" data-mp="ph0mbzom2lvq" />
      <span className="min-w-0 text-left" data-id="element-276" data-mp="ugvhifff5xml">
        {label && <span className={`block text-[10px] font-semibold uppercase tracking-wider ${highlighted ? 'text-azure' : 'text-muted'}`} data-id="element-277" data-mp="fouy911hcolt">{label}</span>}
        <span className={`block truncate text-sm font-semibold ${highlighted ? 'text-white' : 'text-navy'}`} data-id="element-278" data-mp="kmwi3o6qcryc">{name}</span>
        <span className={`block truncate text-xs ${highlighted ? 'text-white/70' : 'text-muted'}`} data-id="element-279" data-mp="bo19y6k4l7se">{employee.jobTitle}</span>
      </span>
    </>;
  const base = `flex items-center gap-3 rounded-2xl px-4 py-3 ${compact ? 'w-full' : 'w-72'} ${highlighted ? 'bg-navy shadow-lift' : 'bg-white ring-1 ring-inset ring-slate-200'}`;
  if (!onClick) return <div className={base} data-id="element-280" data-mp="1r5kze5r1qq8k">{content}</div>;
  return <button type="button" onClick={onClick} className={`${base} transition hover:ring-azure/40 hover:shadow-soft`} data-id="element-281" data-mp="1w4o3nnz7csfh">
      {content}
    </button>;
}
function Connector() {
  return <span className="my-1 h-6 w-px bg-slate-300" aria-hidden="true" data-id="element-282" data-mp="3bn3vxd0x9sw4" />;
}